/**
 * Renderer-independent scene geometry. Coordinates are inclusive fractions of
 * the 16:9 scene, so this contract has no dependency on a rendered pixel size.
 */
export type NormalizedCoordinate = number
export type NormalizedPoint = Readonly<{ x: NormalizedCoordinate; y: NormalizedCoordinate }>
export type NormalizedRect = Readonly<{ left: number; top: number; right: number; bottom: number }>
export type SpriteOrientation = 'left-to-right' | 'right-to-left' | 'facing-left' | 'facing-right' | 'neutral'

export type FishingSceneAnchorId = 'sign-face' | 'sign-top-edge' | 'sign-post' | 'bucket-rim' | 'dock-left-post' | 'dock-right-post' | 'foreground-stone'
export type FishingSceneZoneId = 'safe-foreground-stones' | 'badger-foreground' | 'water' | 'nessie-open-water' | 'sky' | 'left-shore' | 'right-shore' | 'sign-surface' | 'sign-top-edge-surface' | 'sign-support-surface' | 'bucket-surface' | 'dock-left-post-surface' | 'dock-right-post-surface' | 'right-forest-exclusion'

export const fishingSceneAnchors: Readonly<Record<FishingSceneAnchorId, NormalizedPoint>> = {
  'sign-face': { x: 0.12, y: 0.67 },
  'sign-top-edge': { x: 0.12, y: 0.614 },
  'sign-post': { x: 0.122, y: 0.765 },
  'bucket-rim': { x: 0.335, y: 0.815 },
  'dock-left-post': { x: 0.359, y: 0.579 },
  'dock-right-post': { x: 0.524, y: 0.607 },
  // Measured from the crown of the isolated foreground water rock in the
  // canonical 1672×941 lake composition.
  'foreground-stone': { x: 0.718, y: 0.925 },
} as const

export const fishingSceneZones: Readonly<Record<FishingSceneZoneId, NormalizedRect>> = {
  // The right-bank stones are deliberately separate from the dock footprint.
  'safe-foreground-stones': { left: 0.695, top: 0.895, right: 0.965, bottom: 0.985 },
  'badger-foreground': { left: 0.005, top: 0.77, right: 0.31, bottom: 0.995 },
  // This deliberately stops before the forest silhouette, including the
  // largest Nessie scale and its ripple footprint.
  water: { left: 0.34, top: 0.4, right: 0.74, bottom: 0.76 },
  'nessie-open-water': { left: 0.56, top: 0.48, right: 0.74, bottom: 0.8 },
  sky: { left: 0.04, top: 0.06, right: 0.75, bottom: 0.38 },
  'left-shore': { left: 0.04, top: 0.48, right: 0.28, bottom: 0.78 },
  'right-shore': { left: 0.62, top: 0.5, right: 0.76, bottom: 0.76 },
  'sign-surface': { left: 0.052, top: 0.606, right: 0.19, bottom: 0.735 },
  'sign-top-edge-surface': { left: 0.052, top: 0.588, right: 0.19, bottom: 0.625 },
  'sign-support-surface': { left: 0.105, top: 0.715, right: 0.139, bottom: 0.842 },
  'bucket-surface': { left: 0.3, top: 0.755, right: 0.37, bottom: 0.88 },
  'dock-left-post-surface': { left: 0.346, top: 0.574, right: 0.374, bottom: 0.65 },
  'dock-right-post-surface': { left: 0.508, top: 0.601, right: 0.542, bottom: 0.69 },
  // The forest silhouette occupies this area at every responsive scene size.
  'right-forest-exclusion': { left: 0.74, top: 0.35, right: 1, bottom: 0.82 },
} as const

export type MicroLifeKind = 'birds' | 'distant-sparrow-flock' | 'fish-jump' | 'badger' | 'nessie' | 'dragonfly' | 'distant-heron'
  | 'beetle-on-sign' | 'butterfly-on-bucket' | 'butterfly-on-sign' | 'dragonfly-on-dock-post' | 'ladybug-on-sign-support' | 'frog-in-foreground-stones'
export type MicroLifePlacementId = 'bird-sky' | 'fish-water' | 'badger-shore' | 'nessie-water' | 'dragonfly-shore' | 'heron-shore'
  | 'beetle-sign' | 'butterfly-bucket' | 'butterfly-sign' | 'dragonfly-dock-post' | 'ladybug-sign-support' | 'frog-foreground-stones'
export type MicroLifePlacement = Readonly<{
  id: MicroLifePlacementId
  allowedZones: readonly FishingSceneZoneId[]
  allowedAnchors?: readonly FishingSceneAnchorId[]
  center: NormalizedPoint
  footprint: NormalizedRect
  spriteScale: number
  orientation: SpriteOrientation
  rippleFootprint?: NormalizedRect
}>

type PlacementSpec = Readonly<{
  id: MicroLifePlacementId
  zones: readonly FishingSceneZoneId[]
  footprintSize: Readonly<{ width: number; height: number }>
  rippleSize?: Readonly<{ width: number; height: number }>
  scale: Readonly<{ minimum: number; maximum: number }>
  orientations: readonly SpriteOrientation[]
  anchors?: readonly FishingSceneAnchorId[]
}>

export const microLifePlacementByKind: Readonly<Record<MicroLifeKind, MicroLifePlacementId>> = {
  birds: 'bird-sky', 'distant-sparrow-flock': 'bird-sky', 'fish-jump': 'fish-water', badger: 'badger-shore', nessie: 'nessie-water', dragonfly: 'dragonfly-shore', 'distant-heron': 'heron-shore',
  'beetle-on-sign': 'beetle-sign', 'butterfly-on-bucket': 'butterfly-bucket', 'butterfly-on-sign': 'butterfly-sign',
  'dragonfly-on-dock-post': 'dragonfly-dock-post', 'ladybug-on-sign-support': 'ladybug-sign-support', 'frog-in-foreground-stones': 'frog-foreground-stones',
} as const

const placementSpecs: Readonly<Record<MicroLifePlacementId, PlacementSpec>> = {
  'bird-sky': { id: 'bird-sky', zones: ['sky'], footprintSize: { width: 0.06, height: 0.06 }, scale: { minimum: 0.72, maximum: 1 }, orientations: ['left-to-right', 'right-to-left'] },
  'fish-water': { id: 'fish-water', zones: ['water'], footprintSize: { width: 0.035, height: 0.1 }, rippleSize: { width: 0.1, height: 0.035 }, scale: { minimum: 0.76, maximum: 1.08 }, orientations: ['neutral'] },
  'badger-shore': { id: 'badger-shore', zones: ['badger-foreground'], footprintSize: { width: 0.18, height: 0.18 }, scale: { minimum: 1, maximum: 1 }, orientations: ['facing-left', 'facing-right'] },
  'nessie-water': { id: 'nessie-water', zones: ['nessie-open-water'], footprintSize: { width: 0.045, height: 0.15 }, rippleSize: { width: 0.09, height: 0.03 }, scale: { minimum: 0.82, maximum: 1.08 }, orientations: ['neutral'] },
  'dragonfly-shore': { id: 'dragonfly-shore', zones: ['left-shore'], footprintSize: { width: 0.04, height: 0.04 }, scale: { minimum: 0.8, maximum: 1 }, orientations: ['left-to-right', 'right-to-left'] },
  'heron-shore': { id: 'heron-shore', zones: ['right-shore'], footprintSize: { width: 0.06, height: 0.16 }, scale: { minimum: 0.85, maximum: 1 }, orientations: ['facing-left'] },
  'beetle-sign': { id: 'beetle-sign', zones: ['sign-surface'], footprintSize: { width: 0.015, height: 0.02 }, scale: { minimum: 0.8, maximum: 1 }, orientations: ['neutral'], anchors: ['sign-face'] },
  'butterfly-bucket': { id: 'butterfly-bucket', zones: ['bucket-surface'], footprintSize: { width: 0.025, height: 0.025 }, scale: { minimum: 0.8, maximum: 1 }, orientations: ['neutral'], anchors: ['bucket-rim'] },
  'butterfly-sign': { id: 'butterfly-sign', zones: ['sign-top-edge-surface'], footprintSize: { width: 0.025, height: 0.025 }, scale: { minimum: 0.8, maximum: 1 }, orientations: ['neutral'], anchors: ['sign-top-edge'] },
  'dragonfly-dock-post': { id: 'dragonfly-dock-post', zones: ['dock-right-post-surface'], footprintSize: { width: 0.025, height: 0.02 }, scale: { minimum: 0.8, maximum: 1 }, orientations: ['neutral'], anchors: ['dock-right-post'] },
  'ladybug-sign-support': { id: 'ladybug-sign-support', zones: ['sign-support-surface'], footprintSize: { width: 0.012, height: 0.018 }, scale: { minimum: 0.8, maximum: 1 }, orientations: ['neutral'], anchors: ['sign-post'] },
  'frog-foreground-stones': { id: 'frog-foreground-stones', zones: ['safe-foreground-stones'], footprintSize: { width: 0.05, height: 0.04 }, scale: { minimum: 0.9, maximum: 1 }, orientations: ['neutral'], anchors: ['foreground-stone'] },
} as const

export function isNormalizedPoint(point: NormalizedPoint): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.y) && point.x >= 0 && point.x <= 1 && point.y >= 0 && point.y <= 1
}

export function isNormalizedRect(rect: NormalizedRect): boolean {
  return [rect.left, rect.top, rect.right, rect.bottom].every(Number.isFinite)
    && rect.left >= 0 && rect.top >= 0 && rect.right <= 1 && rect.bottom <= 1 && rect.left <= rect.right && rect.top <= rect.bottom
}

export function containsRect(zone: NormalizedRect, footprint: NormalizedRect): boolean {
  return footprint.left >= zone.left && footprint.right <= zone.right && footprint.top >= zone.top && footprint.bottom <= zone.bottom
}

function containsPoint(zone: NormalizedRect, point: NormalizedPoint): boolean {
  return point.x >= zone.left && point.x <= zone.right && point.y >= zone.top && point.y <= zone.bottom
}

export function intersectsRect(first: NormalizedRect, second: NormalizedRect): boolean {
  return first.left < second.right && first.right > second.left && first.top < second.bottom && first.bottom > second.top
}

function footprintAt(center: NormalizedPoint, size: { width: number; height: number }, scale: number): NormalizedRect {
  const width = size.width * scale
  const height = size.height * scale
  return { left: center.x - width / 2, top: center.y - height / 2, right: center.x + width / 2, bottom: center.y + height / 2 }
}

export function validateMicroLifePlacement(placement: MicroLifePlacement): string | null {
  if (!isNormalizedPoint(placement.center) || !isNormalizedRect(placement.footprint) || !Number.isFinite(placement.spriteScale) || placement.spriteScale <= 0) return 'invalid-normalized-geometry'
  const insideAllowedZone = placement.allowedAnchors
    ? placement.allowedZones.some((zone) => containsPoint(fishingSceneZones[zone], placement.center))
    : placement.allowedZones.some((zone) => containsRect(fishingSceneZones[zone], placement.footprint))
  if (!insideAllowedZone) return 'outside-allowed-zone'
  const rippleFootprint = placement.rippleFootprint
  if (rippleFootprint && (!isNormalizedRect(rippleFootprint) || !placement.allowedZones.some((zone) => containsRect(fishingSceneZones[zone], rippleFootprint)))) return 'ripple-outside-allowed-zone'
  const forest = fishingSceneZones['right-forest-exclusion']
  if (intersectsRect(placement.footprint, forest) || (rippleFootprint && intersectsRect(rippleFootprint, forest))) return 'forest-exclusion'
  return null
}

function samePoint(first: NormalizedPoint, second: NormalizedPoint): boolean {
  return Math.abs(first.x - second.x) < Number.EPSILON && Math.abs(first.y - second.y) < Number.EPSILON
}

export function findFishingSceneAnchor(
  center: NormalizedPoint,
  allowedAnchors: readonly FishingSceneAnchorId[] = [],
): FishingSceneAnchorId | null {
  return allowedAnchors.find((anchorId) => samePoint(center, fishingSceneAnchors[anchorId])) ?? null
}

/** Validates the kind, safe zone, and exact authored anchor at the renderer boundary. */
export function validateMicroLifePlacementForKind(kind: unknown, placement: MicroLifePlacement): string | null {
  if (typeof kind !== 'string' || !(kind in microLifePlacementByKind)) return 'unknown-micro-life-kind'
  const spec = placementSpecs[microLifePlacementByKind[kind as MicroLifeKind]]
  if (placement.id !== spec.id) return 'placement-kind-mismatch'
  if (placement.allowedZones.length !== spec.zones.length
    || placement.allowedZones.some((zoneId, index) => zoneId !== spec.zones[index])) return 'placement-zone-mismatch'
  const geometryError = validateMicroLifePlacement(placement)
  if (geometryError) return geometryError
  if (!spec.anchors) return placement.allowedAnchors ? 'unexpected-anchor-class' : null
  if (!placement.allowedAnchors || placement.allowedAnchors.length !== spec.anchors.length
    || placement.allowedAnchors.some((anchorId, index) => anchorId !== spec.anchors?.[index])) return 'anchor-class-mismatch'
  return findFishingSceneAnchor(placement.center, spec.anchors) ? null : 'unsafe-anchor-contact'
}

/** Returns null for unknown event identifiers and for candidates that do not fit safely. */
export function resolveMicroLifePlacement(kind: unknown, random: () => number): MicroLifePlacement | null {
  if (typeof kind !== 'string' || !(kind in microLifePlacementByKind)) return null
  const placementId = microLifePlacementByKind[kind as MicroLifeKind]
  const spec = placementSpecs[placementId]
  const zone = fishingSceneZones[spec.zones[0]]
  const scale = spec.scale.minimum + random() * (spec.scale.maximum - spec.scale.minimum)
  const halfWidth = spec.footprintSize.width * scale / 2
  const halfHeight = spec.footprintSize.height * scale / 2
  const rippleHalfWidth = spec.rippleSize ? spec.rippleSize.width * scale / 2 : 0
  const rippleHalfHeight = spec.rippleSize ? spec.rippleSize.height * scale / 2 : 0
  const horizontalInset = Math.max(halfWidth, rippleHalfWidth)
  const anchor = spec.anchors?.[Math.min(spec.anchors.length - 1, Math.floor(random() * spec.anchors.length))]
  const center = anchor
    ? fishingSceneAnchors[anchor]
    : {
    x: zone.left + horizontalInset + random() * (zone.right - zone.left - horizontalInset * 2),
    y: zone.top + halfHeight + random() * (zone.bottom - zone.top - halfHeight * 2 - rippleHalfHeight),
  }
  const orientation = spec.orientations[Math.min(spec.orientations.length - 1, Math.floor(random() * spec.orientations.length))]
  const footprint = footprintAt(center, spec.footprintSize, scale)
  const rippleFootprint = spec.rippleSize ? footprintAt({ x: center.x, y: footprint.bottom }, spec.rippleSize, scale) : undefined
  const placement: MicroLifePlacement = { id: placementId, allowedZones: spec.zones, ...(spec.anchors ? { allowedAnchors: spec.anchors } : {}), center, footprint, spriteScale: scale, orientation, ...(rippleFootprint ? { rippleFootprint } : {}) }
  return validateMicroLifePlacement(placement) === null ? placement : null
}

function seededPlacementRandom(value: string): () => number {
  let state = 2_166_136_261
  for (let index = 0; index < value.length; index += 1) state = Math.imul(state ^ value.charCodeAt(index), 16_777_619)
  return () => {
    state += 0x6D2B79F5
    let next = state
    next = Math.imul(next ^ (next >>> 15), next | 1)
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61)
    return ((next ^ (next >>> 14)) >>> 0) / 4_294_967_296
  }
}

/** Shared and legacy schedulers attach the same id-seeded, validated presentation. */
export function createMicroLifePresentation(kind: unknown, eventId: string): MicroLifePlacement | null {
  return resolveMicroLifePlacement(kind, seededPlacementRandom(eventId))
}
